import React from 'react';
import styled from 'styled-components';

/**
 * FreakIT 기업 사이트 개인정보처리방침.
 * 이 사이트는 회원가입/로그인이 없으며, '문의하기' 양식을 통해서만
 * 개인정보를 수집합니다. SOCRATEST 제품 약관과 별개로, 문의 접수 범위에
 * 한정하여 작성되었습니다.
 */
const PrivacyPolicyPage: React.FC = () => {
  return (
    <PageContainer>
      <Inner>
        <PageTitle>개인정보처리방침</PageTitle>
        <Intro>
          FreakIT(이하 ‘회사’)는 개인정보 보호법 제30조에 따라 정보주체의 개인정보를
          보호하고 이와 관련한 고충을 신속하고 원활하게 처리할 수 있도록 다음과 같이
          개인정보처리방침을 수립·공개합니다. 본 사이트는 회원가입·로그인 기능이 없으며,
          ‘문의하기’ 양식을 통한 문의 접수 목적으로만 개인정보를 수집합니다.
        </Intro>

        <Article>
          <ArticleTitle>제1조 (개인정보의 처리 목적)</ArticleTitle>
          <Paragraph>
            회사는 다음의 목적을 위하여 개인정보를 처리하며, 해당 목적 이외의 용도로는
            이용하지 않습니다.
          </Paragraph>
          <List>
            <ListItem>문의 사항의 접수, 본인 확인 및 답변·안내 제공</ListItem>
            <ListItem>고충 처리 및 분쟁 대응을 위한 기록 보존</ListItem>
          </List>
        </Article>

        <Article>
          <ArticleTitle>제2조 (처리하는 개인정보의 항목)</ArticleTitle>
          <List>
            <ListItem>
              <b>문의하기 양식(필수):</b> 성명, 이메일 주소, 연락처, 문의 제목 및 내용
            </ListItem>
            <ListItem>
              <b>자동 생성·수집 정보:</b> 접속 IP, 쿠키, 서비스 이용 기록, 접속 일시 등이
              서비스 이용 과정에서 자동으로 생성되어 수집될 수 있습니다.
            </ListItem>
          </List>
        </Article>

        <Article>
          <ArticleTitle>제3조 (개인정보의 보유 및 이용 기간)</ArticleTitle>
          <Paragraph>
            회사는 법령에 따른 보유·이용 기간 또는 정보주체로부터 동의받은 기간 내에서
            개인정보를 처리·보유합니다.
          </Paragraph>
          <List>
            <ListItem>
              문의 접수·처리에 관한 기록: 답변 완료 후 3년간 보관 후 파기
              (전자상거래 등에서의 소비자보호에 관한 법률상 소비자 불만 또는 분쟁처리에
              관한 기록 보존 기준)
            </ListItem>
          </List>
        </Article>

        <Article>
          <ArticleTitle>제4조 (개인정보의 제3자 제공)</ArticleTitle>
          <Paragraph>
            회사는 정보주체의 개인정보를 제1조의 목적 범위 내에서만 처리하며, 정보주체의
            별도 동의, 법률의 특별한 규정 등 개인정보 보호법 제17조·제18조에 해당하는
            경우를 제외하고는 제3자에게 제공하지 않습니다.
          </Paragraph>
        </Article>

        <Article>
          <ArticleTitle>제5조 (개인정보 처리의 위탁 및 국외 이전)</ArticleTitle>
          <Paragraph>
            회사는 문의 메일의 전송·처리를 위하여 아래와 같이 개인정보 처리 업무를
            위탁(국외 이전)하고 있습니다.
          </Paragraph>
          <List>
            <ListItem>
              <b>EmailJS (EmailJS, Inc.) — 문의 메일 전송</b>
              <SubList>
                <SubListItem>이전 항목: 성명, 이메일 주소, 연락처, 문의 내용</SubListItem>
                <SubListItem>이전 국가: 미국</SubListItem>
                <SubListItem>이전 목적: 문의 양식 내용의 이메일 전송</SubListItem>
                <SubListItem>보유 기간: 전송 처리 후 별도 저장하지 않음</SubListItem>
              </SubList>
            </ListItem>
          </List>
        </Article>

        <Article>
          <ArticleTitle>제6조 (정보주체의 권리·의무 및 행사 방법)</ArticleTitle>
          <List>
            <ListItem>
              정보주체는 언제든지 개인정보의 열람·정정·삭제·처리정지를 요구할 수 있으며,
              이는 freakit2025@gmail.com을 통해 신청할 수 있습니다.
            </ListItem>
            <ListItem>
              회사는 권리 행사 요청에 대해 지체 없이 조치하며, 본인 또는 정당한 대리인인지
              확인합니다.
            </ListItem>
          </List>
        </Article>

        <Article>
          <ArticleTitle>제7조 (개인정보의 파기)</ArticleTitle>
          <List>
            <ListItem>
              <b>파기 절차·기한:</b> 보유 기간이 경과하거나 처리 목적이 달성되어 개인정보가
              불필요하게 된 경우, 불필요하게 된 날로부터 5일 이내에 파기합니다.
            </ListItem>
            <ListItem>
              <b>파기 방법:</b> 전자적 파일은 복구·재생이 불가능한 기술적 방법으로 삭제하며,
              출력물은 분쇄 또는 소각합니다.
            </ListItem>
          </List>
        </Article>

        <Article>
          <ArticleTitle>제8조 (개인정보의 안전성 확보 조치)</ArticleTitle>
          <List>
            <ListItem>
              <b>관리적 조치:</b> 내부관리계획 수립·시행, 개인정보 취급자에 대한 정기 교육
            </ListItem>
            <ListItem>
              <b>기술적 조치:</b> 개인정보 접근 권한 관리, 전송 구간 암호화, 보안 프로그램 운영
            </ListItem>
          </List>
        </Article>

        <Article>
          <ArticleTitle>제9조 (쿠키의 운영 및 거부)</ArticleTitle>
          <Paragraph>
            회사는 서비스 이용 분석 등을 위해 쿠키를 사용할 수 있습니다. 정보주체는 웹
            브라우저 설정을 통해 쿠키 저장을 거부할 수 있으며, 이 경우 일부 기능 이용에
            제한이 있을 수 있습니다.
          </Paragraph>
        </Article>

        <Article>
          <ArticleTitle>제10조 (개인정보 보호책임자)</ArticleTitle>
          <Paragraph>
            회사는 개인정보 처리에 관한 업무를 총괄하여 책임지고, 정보주체의 불만 처리 및
            피해 구제를 위하여 아래와 같이 개인정보 보호책임자를 지정하고 있습니다.
          </Paragraph>
          <List>
            <ListItem>성명: 이도운 (대표)</ListItem>
            <ListItem>연락처: 010-7568-3302</ListItem>
            <ListItem>이메일: freakit2025@gmail.com</ListItem>
          </List>
        </Article>

        <Article>
          <ArticleTitle>제11조 (권익침해 구제 방법)</ArticleTitle>
          <Paragraph>
            정보주체는 개인정보 침해로 인한 구제를 받기 위하여 아래 기관에 분쟁 해결이나
            상담 등을 신청할 수 있습니다.
          </Paragraph>
          <List>
            <ListItem>개인정보 분쟁조정위원회: (국번 없이) 1833-6972 (www.kopico.go.kr)</ListItem>
            <ListItem>개인정보침해신고센터: (국번 없이) 118 (privacy.kisa.or.kr)</ListItem>
            <ListItem>대검찰청: (국번 없이) 1301 (www.spo.go.kr)</ListItem>
            <ListItem>경찰청: (국번 없이) 182 (ecrm.cyber.go.kr)</ListItem>
          </List>
        </Article>

        <Article>
          <ArticleTitle>제12조 (개인정보처리방침의 변경)</ArticleTitle>
          <Paragraph>
            본 개인정보처리방침은 2026년 6월 30일부터 적용됩니다. 법령·방침의 변경에 따라
            내용이 추가·삭제·수정될 경우 시행 전 홈페이지를 통해 공지합니다.
          </Paragraph>
        </Article>
      </Inner>
    </PageContainer>
  );
};

export default PrivacyPolicyPage;

/* ────────── Styled Components ────────── */

const PageContainer = styled.div`
  padding-top: 80px;
`;

const Inner = styled.div`
  max-width: 860px;
  margin: 0 auto;
  padding: 4rem 2rem 5rem;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    padding: 3rem 1.5rem 4rem;
  }
`;

const PageTitle = styled.h1`
  font-size: 2.4rem;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.textPrimary};
  margin-bottom: 1.5rem;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    font-size: 1.9rem;
  }
`;

const Intro = styled.p`
  font-size: 1rem;
  line-height: 1.9;
  color: ${({ theme }) => theme.colors.textSecondary};
  padding-bottom: 2rem;
  margin-bottom: 2.5rem;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`;

const Article = styled.section`
  margin-bottom: 2.5rem;
`;

const ArticleTitle = styled.h2`
  font-size: 1.2rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.textPrimary};
  margin-bottom: 1rem;
`;

const Paragraph = styled.p`
  font-size: 0.98rem;
  line-height: 1.9;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: 0.75rem;

  b {
    color: ${({ theme }) => theme.colors.textPrimary};
    font-weight: 700;
  }
`;

const List = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 0.7rem;
`;

const ListItem = styled.li`
  font-size: 0.98rem;
  line-height: 1.85;
  color: ${({ theme }) => theme.colors.textSecondary};
  padding-left: 1.1rem;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0.7em;
    width: 5px;
    height: 5px;
    border-radius: 50%;
    background: ${({ theme }) => theme.colors.accent};
  }

  b {
    color: ${({ theme }) => theme.colors.textPrimary};
    font-weight: 700;
  }
`;

const SubList = styled.ul`
  list-style: none;
  margin: 0.5rem 0 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
`;

const SubListItem = styled.li`
  font-size: 0.92rem;
  line-height: 1.7;
  color: ${({ theme }) => theme.colors.textTertiary};
  padding-left: 1rem;
  position: relative;

  &::before {
    content: '–';
    position: absolute;
    left: 0;
    color: ${({ theme }) => theme.colors.textTertiary};
  }
`;
